import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { emptySignature, signatureIsMeaningful, signaturePath, withStroke, type SignatureStrokes } from '../../domain/signature';
import { Button } from './Button';

// Hand-drawn signature pad (Round 2 §C): an SVG surface that records pointer strokes as vector
// points (src/domain/signature.ts). No canvas — works in every WebView and in jsdom, and the mark
// renders crisply on screen and in the agreement PDF. Clear always available (OC §9.4).
export const SIGNATURE_PAD_HEIGHT = 160;

export function SignaturePad(props: {
  value: SignatureStrokes | null;
  onChange: (next: SignatureStrokes | null) => void;
  clearLabel: string;
  hint: string;
  disabled?: boolean;
  // false = the consumer renders its own Clear (the Owner-selected sign sheet puts it in the block header).
  showClear?: boolean;
  testId?: string;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const svg = useRef<SVGSVGElement | null>(null);
  const [width, setWidth] = useState(320);
  const drawing = useRef<number[] | null>(null);
  const [live, setLive] = useState<number[] | null>(null);
  const value = props.value ?? emptySignature(width, SIGNATURE_PAD_HEIGHT);

  useEffect(() => {
    const el = host.current;
    if (!el) return undefined;
    const measure = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  function local(event: ReactPointerEvent<SVGSVGElement>): [number, number] {
    const rect = svg.current?.getBoundingClientRect();
    const x = event.clientX - (rect?.left ?? 0);
    const y = event.clientY - (rect?.top ?? 0);
    return [Math.round(x * 10) / 10, Math.round(y * 10) / 10];
  }

  function onDown(event: ReactPointerEvent<SVGSVGElement>) {
    if (props.disabled) return;
    event.preventDefault();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // capture is a nicety
    }
    drawing.current = [...local(event)];
    setLive(drawing.current);
  }

  function onMove(event: ReactPointerEvent<SVGSVGElement>) {
    if (!drawing.current || props.disabled) return;
    event.preventDefault();
    drawing.current = [...drawing.current, ...local(event)];
    setLive(drawing.current);
  }

  function onUp() {
    const stroke = drawing.current;
    drawing.current = null;
    setLive(null);
    if (!stroke || stroke.length < 2) return;
    const base = props.value && props.value.width === width ? props.value : { ...emptySignature(width, SIGNATURE_PAD_HEIGHT), strokes: props.value?.strokes ?? [] };
    props.onChange(withStroke(base, stroke));
  }

  const path = signaturePath({ ...value, strokes: live ? [...value.strokes, live] : value.strokes });
  const signed = signatureIsMeaningful(props.value);

  return (
    <div className="signature-pad" ref={host} data-testid={props.testId} data-signed={signed ? 'true' : 'false'}>
      <svg
        ref={svg}
        className={`signature-surface ${props.disabled ? 'signature-surface-disabled' : ''}`}
        viewBox={`0 0 ${value.width} ${value.height}`}
        width="100%"
        height={SIGNATURE_PAD_HEIGHT}
        role="img"
        aria-label={props.hint}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
        onPointerCancel={onUp}
        data-testid={props.testId ? `${props.testId}-surface` : undefined}
      >
        <line className="signature-baseline" x1="12" y1={value.height - 28} x2={value.width - 12} y2={value.height - 28} />
        {path.length > 0 ? <path className="signature-ink" d={path} /> : null}
        {path.length === 0 ? (
          <text className="signature-hint" x={value.width / 2} y={value.height / 2 + 6} textAnchor="middle">
            {props.hint}
          </text>
        ) : null}
      </svg>
      {props.showClear === false ? null : (
        <div className="signature-tools">
          <Button variant="text" size="small" block={false} onClick={() => props.onChange(null)} disabled={props.disabled || !props.value || props.value.strokes.length === 0} testId={props.testId ? `${props.testId}-clear` : undefined}>
            {props.clearLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
